import { Box, Stack, TextField } from '@mui/material';
import { useDocumentationCard } from '../../building-blocks/documentation-card/useDocumentationCard';
import { Panel } from '../../building-blocks/documentation-card/header/Panel';
import { BackButton } from '../../building-blocks/documentation-card/header/BackButton';
import {
  __SWEEP_DESIGN_TYPOGRAPHY_STYLES___,
  colors,
  IconButton,
  Tag as TagComponent,
  Tooltip,
  Typography,
} from '@sweep-io/sweep-design';
import { Tag } from '../../../../apis/facades/useUserInputsApiFacade';
import { ActionOption, ButtonWithActionMenu } from './atoms/ButtonWithActionMenu';
import { useCallback, useMemo, useState } from 'react';
import { useTags } from '../annotations/tags/useTags';
import ConfirmDestructiveDialog from '../../../common/dialogs/ConfirmDestructiveDialog';
import useConfirm from '../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { DotDivider } from '../../../common/DotDivider';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { DocumentationCardSearchInput } from '../../building-blocks/documentation-card/search/DocumentationCardSearchInput';
import { DocumentationCardSearchEntryIcon } from '../../building-blocks/documentation-card/search/DocumentationCardSearchEntryIcon';
import { ConfigurationItemMap } from '../../configuration-item-tables/shared/helpers';
import { downloadCSV } from '../../shared/csv/downloadCSV';
import { addStatusAndUpdateType } from '../../shared/csv/utils';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import usePaywall from '../../../dashboard/paywall/usePaywall';
import DownloadCsvPaywallBlock from '../../shared/csv/DownloadCsvPaywallBlock';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { ACTIONS_EVENTS } from '../../../../services/events';
import { Download } from '@sweep-io/sweep-design/dist/icons';
import { humanizeDate } from '../../../helpers/humanizeDate';
import { FIELD } from '@server/parser';

const HEADER_TYPOGRAPHY = 'h4-medium';

const CSV_HEADERS = [
  { label: 'Label', value: 'label' },
  { label: 'API Name', value: 'name' },
  { label: 'Object', value: 'objectNames' },
  { label: 'Description', value: 'description' },
  { label: 'Error Message', value: 'errorMessage' },
  { label: 'Type', value: 'parentType' },
  { label: 'Field Type', value: 'fieldType' },
  { label: 'Status', value: 'status' },
  { label: 'Created By', value: 'createdBy' },
  { label: 'Created At', value: 'createdAt' },
  { label: 'Updated By', value: 'updatedBy' },
  { label: 'Updated At', value: 'updatedAt' },
];

interface TagsHeaderProps {
  tag: Tag;
  crmOrgId: string;
  tagType: TagType;
  userEmail?: string;
  configurationItems?: ConfigurationItemMap;
}

export enum TagType {
  user = 'user',
  tag = 'tag',
}

const deleteTexts = {
  [TagType.user]: {
    cta: 'Unlink user',
    text: 'Unlinking this user will remove their associations from all configurations where they are assigned.',
  },
  [TagType.tag]: {
    cta: 'Delete tag',
    text: 'Deleting this tag will remove it from all configurations where its used.',
  },
};

export const TagsHeader = ({
  tag,
  crmOrgId,
  tagType,
  userEmail,
  configurationItems,
}: TagsHeaderProps) => {
  const [editName, setEditName] = useState<string>();
  const [isDelete, setIsDelete] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);

  const { openConfirm, onConfirm, onCancel } = useConfirm();

  const { onDeleteTag, onUpdateTag } = useTags(crmOrgId);
  const { onGoBackInHistory, historyLength } = useDocumentationCard();
  const { searchInDocumentationCard } = useFeatureToggle();
  const { addNotification } = useSweepNotifications();
  const { isPaywallActive } = usePaywall();
  const sendBiEvent = useSendBiEvent();

  const csvRows = useMemo(() => {
    if (!configurationItems) return [];
    const allItems = Object.values(configurationItems).flat();
    return allItems.map((item) => ({
      ...item,
      ...addStatusAndUpdateType([item])[0],
      objectNames: item.objectApiNames?.join(', ') ?? '',
      fieldType: item.parentType === FIELD ? ((item as any).fieldType ?? '') : '',
      errorMessage: (item as any).errorMessage ?? '',
      createdAt: humanizeDate({ dateOrTimestamp: item.createdAt ?? '' }),
      updatedAt: humanizeDate({ dateOrTimestamp: item.updatedAt ?? '' }),
    }));
  }, [configurationItems]);

  const handleDownloadCsv = useCallback(() => {
    if (isPaywallActive) {
      setIsPaywallOpen(true);
      return;
    }

    const downloadedSuccessfully = downloadCSV({
      fileName: `${tag.name}_Configurations_Export_from_Sweep`,
      headers: CSV_HEADERS,
      rows: csvRows,
    });

    if (!downloadedSuccessfully) {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Nothing to download',
      });
    } else {
      sendBiEvent({
        name: ACTIONS_EVENTS.documentationExportCsv,
        props: { exportType: 'tag_configurations', numberOfRecords: csvRows.length },
      });
    }
  }, [isPaywallActive, csvRows, tag.name, addNotification, sendBiEvent]);

  const isTag = tagType === TagType.tag;
  const isUser = tagType === TagType.user;
  const noEditActions = isUser;

  const updateName = (newName?: string) => {
    if (newName !== tag.name && newName) {
      const newTag = { ...tag, name: newName };
      onUpdateTag(newTag);
    }

    setEditName(undefined);
  };

  const updateColor = useCallback(
    (newColor: string) => {
      if (newColor !== tag.color) {
        const newTag = { ...tag, color: newColor };
        onUpdateTag(newTag);
      }
    },
    [tag, onUpdateTag],
  );

  return (
    <Panel sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        gap={1}
        alignItems="center"
        width="100%"
      >
        {historyLength > 0 && <BackButton onClick={onGoBackInHistory} />}
        <Box flex={1} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box display="flex" flexDirection="column" gap={1} width="100%">
            {!editName && (
              <Box
                onClick={() => !noEditActions && setEditName(tag.name)}
                sx={{ cursor: noEditActions ? 'default' : 'pointer' }}
              >
                <Typography
                  variant={HEADER_TYPOGRAPHY}
                  whiteSpace="break-spaces"
                  wordBreak="break-word"
                >
                  {tag.name}
                </Typography>
              </Box>
            )}

            {Boolean(editName) && (
              <TextField
                disabled={!editName}
                fullWidth
                variant="standard"
                value={editName ?? tag.name}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && updateName(editName)}
                onBlur={() => editName && updateName(editName)}
                sx={{
                  whiteSpace: 'break-spaces',
                  wordBreak: 'break-word',
                  input: {
                    fontSize: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[HEADER_TYPOGRAPHY]?.fontSize,
                    fontWeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[HEADER_TYPOGRAPHY]?.fontWeight,
                    lineHeight: __SWEEP_DESIGN_TYPOGRAPHY_STYLES___[HEADER_TYPOGRAPHY]?.lineHeight,
                    padding: 0,
                  },
                }}
              />
            )}

            {isTag && (
              <Typography variant="caption" color={colors.grey[700]}>
                Tag
              </Typography>
            )}

            {isUser && (
              <Stack direction="row" alignItems="center" gap={1}>
                <TagComponent label="Sweep User" />
                <DotDivider />
                <Typography variant="caption" color={colors.grey[700]}>
                  {userEmail}
                </Typography>
              </Stack>
            )}
          </Box>

          <Stack direction="row" gap={1} alignItems="center">
            {searchInDocumentationCard && <DocumentationCardSearchEntryIcon />}

            {isPaywallOpen && (
              <DownloadCsvPaywallBlock closeDialog={() => setIsPaywallOpen(false)} />
            )}
            <Tooltip title="Download as CSV">
              <IconButton onClick={handleDownloadCsv} variant="flat" size="tiny">
                <Download />
              </IconButton>
            </Tooltip>

            <ButtonWithActionMenu
              deleteTitle={deleteTexts[tagType].cta}
              options={
                noEditActions
                  ? [ActionOption.delete]
                  : [ActionOption.changeName, ActionOption.changeColor, ActionOption.delete]
              }
              onChangeName={() => {
                setEditName(tag.name);
              }}
              onChangeColor={updateColor}
              onDelete={async () => {
                const isConfirmed = await openConfirm(
                  <ConfirmDestructiveDialog
                    open
                    onConfirm={onConfirm}
                    onClose={onCancel}
                    title="Are you sure?"
                    confirmText={deleteTexts[tagType].cta}
                    cancelText="Cancel"
                  >
                    {deleteTexts[tagType].text}
                  </ConfirmDestructiveDialog>,
                );

                if (isConfirmed) {
                  setIsDelete(true);
                  await onDeleteTag(tag.id, tagType);
                  setIsDelete(false);
                  onGoBackInHistory();
                }
              }}
              selectedColor={tag.color}
              showLoader={isDelete}
            />
          </Stack>
        </Box>
      </Stack>
      {searchInDocumentationCard && <DocumentationCardSearchInput />}
    </Panel>
  );
};
